/** @file TopBar: brand, "project › puppet" crumbs, revision, screen and Tile tabs, Undo, lint, Export. */
import type { ReactElement } from 'react';
import type { ExportQuery } from '@zilo/pose3d-api/types/restAdded';
import { TileTabStrip } from '../screens/tile/TileTabStrip';
import type { PuppetScreen, TileRef } from '../state/editorTypes';
import { useEditor } from '../state/useEditor';
import { useEditorStore } from '../state/useEditorStore';
import { Button } from '../ui-kit/Button';
import { Spinner } from '../ui-kit/Spinner';
import { Tabs } from '../ui-kit/Tabs';
import { ExportMenu } from './ExportMenu';
import { exportPuppet } from './exportPuppet';
import { RevisionBadge } from './RevisionBadge';
import { useApi } from './useApi';
import { useProjectName } from './useProjectName';

const SCREENS: { id: PuppetScreen; label: string }[] = [{ id: 'pose', label: 'Pose' }, { id: 'art', label: 'Art' }, { id: 'agent', label: 'Agent' }];

/** The project crumb: its name, a button back to its puppets list. */
function ProjectCrumb({ projectId }: { projectId: string }): ReactElement {
  const navigate = useEditor((s) => s.navigate);
  const name = useProjectName(useApi(), projectId);
  return (
    <>
      <span className="p3-crumb-sep">›</span>
      <Button variant="ghost" title="This project's puppets" onClick={() => { navigate({ screen: 'puppets', projectId }); }}>{name}</Button>
    </>
  );
}

/**
 * The puppet half of the bar: only while a puppet route is shown; the project crumb once the puppet says its project;
 * the Tile tabs after Pose / Art / Agent (`tile` is the shown one).
 */
function PuppetControls({ puppetId, screen, tile }: { puppetId: string; screen: PuppetScreen | 'tile'; tile: TileRef | null }): ReactElement {
  const store = useEditorStore();
  const api = useApi();
  const projectId = useEditor((s) => (s.puppet.id === puppetId ? s.puppet.projectId : null));
  const name = useEditor((s) => s.puppet.doc?.name ?? puppetId);
  const lint = useEditor((s) => s.puppet.lint.length);
  const loading = useEditor((s) => s.puppet.doc === null || s.preview.loading);
  const exportZip = (query?: ExportQuery): void => { exportPuppet(store, api, puppetId, name, query); };
  return (
    <>
      {projectId === null ? null : <ProjectCrumb projectId={projectId} />}
      <span className="p3-crumb-sep">›</span>
      <span className="p3-mono" title="Puppet">{name}</span>
      <RevisionBadge />
      {loading ? <Spinner /> : null}
      <Tabs<PuppetScreen | 'tile'> label="Screens" tabs={SCREENS} active={screen} onSelect={(id) => { if (id !== 'tile') store.getState().navigate({ screen: id, puppetId }); }} />
      <TileTabStrip puppetId={puppetId} active={tile} />
      <span className="p3-spacer" />
      <Button onClick={() => { void store.getState().undo(); }} title="Undo the last batch on the server">Undo</Button>
      <Button onClick={() => { void store.getState().refreshLint(); }} title="Lint warnings (click to refresh)">
        Lint {lint}
      </Button>
      <ExportMenu disabled={api === null} onExport={exportZip} />
    </>
  );
}

/** The 40 px bar at the top of every screen. */
export function TopBar(): ReactElement {
  const route = useEditor((s) => s.route);
  const navigate = useEditor((s) => s.navigate);
  return (
    <header className="p3-topbar">
      <span className="p3-brand">POSE3D</span>
      <Button variant="ghost" onClick={() => { navigate({ screen: 'projects' }); }}>Projects</Button>
      {route.screen === 'puppets' ? <><ProjectCrumb projectId={route.projectId} /><span className="p3-spacer" /></> : null}
      {route.screen === 'pose' || route.screen === 'art' || route.screen === 'agent' || route.screen === 'tile'
        ? <PuppetControls puppetId={route.puppetId} screen={route.screen} tile={route.screen === 'tile' ? route.tile : null} />
        : null}
      {route.screen === 'projects' || route.screen === 'settings' ? <span className="p3-spacer" /> : null}
      <Button variant="ghost" aria-label="Settings" onClick={() => { navigate({ screen: 'settings' }); }}>Settings</Button>
    </header>
  );
}
